import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { MAX_PAYMENT_AMOUNT } from '~/entities/Balance/constants'
import { rubleMaskOptions } from '~/shared/lib/maskito'
import { priceFormat, sanitizePriceNumber } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import { FormProvider, NumericInput } from '~/shared/ui/form'
import { FormValues, paySchema } from './model'

type FormProps = {
  isSubmitting: boolean
  onSuccess: () => void
  onSumChange: (sum?: number) => void
  disable: boolean
  additionalContent?: ReactNode
}

export function PayForm({
  isSubmitting,
  onSuccess,
  onSumChange,
  disable,
  additionalContent,
}: FormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(paySchema),
  })
  const { watch } = form

  const amountForm = watch('amount')

  const amountError = useMemo(() => {
    const amount = sanitizePriceNumber(amountForm || 0)
    let message = ''

    if (amount > MAX_PAYMENT_AMOUNT) {
      message = `Максимальная сумма за разовое пополнение средств ${priceFormat(
        MAX_PAYMENT_AMOUNT,
      )} руб.`
    }

    return message || undefined
  }, [amountForm])

  watch((data, { name, type }) => {
    if (type !== 'change') return
    if (name === 'amount') {
      onSumChange(sanitizePriceNumber(data.amount || ''))
    }
  })

  const handleOnSuccess = () => {
    if (amountError) return
    onSuccess()
  }

  return (
    <FormProvider form={form} onSuccess={handleOnSuccess}>
      <NumericInput
        name='amount'
        label='Сумма для пополнения'
        maskitoOptions={rubleMaskOptions}
        errorText={amountError}
      />

      {additionalContent}

      <Button
        fullWidth
        type='submit'
        variant='limeFlooded'
        size='large'
        loading={isSubmitting}
        disabled={disable}
        data-testid='pay-drawer-submit'
      >
        Пополнить
      </Button>
    </FormProvider>
  )
}
